"use client";
import Slider from "../slider/Slider";
import { Box, Grid, Skeleton, Typography } from "@mui/material";
import Link from "@/node_modules/next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { Loader } from "../MainLoader/Loader";
import "./index.css";
import { LargeNumberLike } from "crypto";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";

interface SubCategoryCardProps {
  subcategories: any;
  categorySlug: any;
  categories: any;
}

const SubCategoryCard: React.FC<SubCategoryCardProps> = ({
  subcategories,
  categorySlug,
  categories,
}) => {
  const [loading, setLoading] = useState(false);
  const [categoryName, setCategoryName] = useState();
  const pathName = usePathname();
  const segments = pathName.split("/");
  const lastSegment = segments.pop();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const router = useRouter();
  const handleLoader = () => {
    setLoading(true);
  };
  const blankFunction = () => {};

  useEffect(() => {
    if (!categories == null || undefined) {
      const category = categories?.find(
        (item: any) => item.slug === categorySlug
      );
      setCategoryName(category?.name);
    } else {
      const urlParts = pathName?.split("/");
      const desiredSegment = urlParts?.[2];
      const category = categories?.find(
        (item: any) => item.slug === desiredSegment
      );
      setCategoryName(category?.name);
    }
  }, [categories, lastSegment, categorySlug, pathName]);

  // Load the scroll position from local storage on mount
  useEffect(() => {
    const savedPosition = localStorage.getItem("scrollPosition");
    if (savedPosition) {
      setScrollPosition(parseInt(savedPosition, 10));
    }
  }, []);

  // Use useEffect to scroll to the saved position
  useEffect(() => {
    if (containerRef?.current && scrollPosition !== 0) {
      containerRef.current.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
    }
  }, [scrollPosition]);

  useEffect(() => {
    const extractSlugs = (data: any) => {
      return data?.map((item: any) => item?.slug);
    };

    const slugs = extractSlugs(categories);
    const isMatch = slugs?.includes(lastSegment);
    if (isMatch) {
      localStorage.setItem("scrollPosition", "0");
    }
  }, [categories, lastSegment]);

  const handleButtonClick = (event: any) => {
    const container = containerRef.current;
    const button = event.target;

    if (container && button) {
      const buttonRect = button.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // Calculate the scroll position to center the button
      const offset =
        buttonRect.left - containerRect.left + container.scrollLeft;
      const buttonWidth = buttonRect.width;
      const containerWidth = containerRect.width;

      const calculatedPosition = offset - containerWidth / 2 + buttonWidth / 2;

      // Save the calculated position to local storage and state
      localStorage.setItem("scrollPosition", calculatedPosition.toString());
      setScrollPosition(calculatedPosition);

      // Scroll the container to the calculated position
      container.scrollTo({
        left: calculatedPosition,
        behavior: "smooth",
      });
    }
  };

  const handleNavigate = (e: any) => {
    e.preventDefault(e);
    setLoading(true);
    router.push("/");
  };

  return (
    <Box
      sx={{
        background: "#15741a",
      }}
    >
      {loading && <Loader />}
      <KeyboardBackspaceIcon
        sx={{
          background: "#fff",
          borderRadius: "1rem",
          padding: "4px",
          fontSize: "28px",
          position: "absolute",
          top: "28px",
          left: "20px",
          transform: "translateY(-50%)",
          cursor: "pointer",
          color: "#000",
          boxShadow: "2px 1px 4px 1px rgba(0, 0, 0, 0.2)",
          zIndex: 100,
          display: { xs: "block", sm: "none" },
        }}
        onClick={handleNavigate}
      />
      <Typography
        sx={{
          color: "#fff",
          margin: "auto",
          paddingTop: "16px",
          paddingBottom: "16px",
          fontWeight: "bold",
          display: { xs: "block", sm: "none" },
          textAlign: "center",
        }}
      >
        {categoryName}
      </Typography>

      <Grid container item md={9.5} sm={9.5} margin="auto">
        <div className="main-slider-bar-salon-at-home" ref={containerRef}>
          {subcategories?.length > 0
            ? subcategories?.map((item: any, index: any) => (
                <Link
                  prefetch={false}
                  style={{ textDecoration: "none", color: "inherit" }}
                  key={"subcategoryItem" + item?.id}
                  href={
                    categorySlug == undefined || categorySlug == ""
                      ? item.slug
                      : categorySlug + "/" + item.slug
                  }
                  onClick={
                    lastSegment !== item.slug.trim().replace(/\s+/g, "%20")
                      ? handleLoader
                      : blankFunction
                  }
                >
                  {" "}
                  <div
                    className={
                      (lastSegment == categorySlug && index === 0) ||
                      lastSegment == item.slug.trim().replace(/\s+/g, "%20")
                        ? "selected-main-slider-bar"
                        : ""
                    }
                    onClick={handleButtonClick}
                  >
                    <Slider
                      url={item.mobileImage?.secureHttpPath}
                      title={item.name}
                    />
                  </div>
                </Link>
              ))
            : Array.from({ length: 10 }).map((_, i) => (
                <Box key={i}>
                  <Skeleton
                    key={i}
                    variant="circular"
                    sx={{
                      margin: "0.5rem auto",
                      width: "50px",
                      height: "50px",
                    }}
                  />
                  <Skeleton
                    key={i}
                    variant="rectangular"
                    sx={{
                      margin: "0.5rem",
                      width: "100px",
                      height: "10px",
                    }}
                  />
                </Box>
              ))}
        </div>
      </Grid>
    </Box>
  );
};

export default SubCategoryCard;
